import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import {
  getAssetStorageInfo,
  getAssetStorageProgressBarStyle,
} from '$/utils/assetStorage';
import { openVipModal } from '$/store/useVipModalStore';
import { trackRoboneoVipClick, trackRoboneoVipShow } from '$/utils/vipPromptTrack';
import { getEnv } from '@/common/agents';
import { getAccessToken } from '@/common/utils/accountUtil';
import { getGid } from '@/utils';
import FeedbackModal from '@/components/FeedbackModal';
import styles from './index.module.scss';

interface NavbarAssetStorageCardProps {
  className?: string;
  variant?: 'expanded' | 'compact';
  /** 首页展开侧栏是否处于实际可见状态，避免隐藏 DOM 误报曝光。 */
  visible?: boolean;
}

type FeedbackEnv = 'pre' | 'beta' | 'release';

/** 将当前运行环境转换为反馈系统支持的环境枚举。 */
function getNavbarAssetStorageFeedbackEnv(): FeedbackEnv {
  const env = getEnv().ENV;
  if (env === 'beta') return 'beta';
  if (env === 'release' || env === 'production' || env === 'prod') return 'release';
  return 'pre';
}

/** 首页侧边栏容量卡片，承接资产库容量展示、订阅升级和最高等级会员反馈入口。 */
function NavbarAssetStorageCard({
  className,
  variant = 'expanded',
  visible = true,
}: NavbarAssetStorageCardProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const userInfo = useOfficialStore((state) => state.userInfo);
  const loadSubscribeInfo = useOfficialStore((state) => state.loadSubscribeInfo);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const requestedSubscribeRef = useRef(false);
  const exposedRef = useRef(false);
  const info = useMemo(() => getAssetStorageInfo(userInfo.subscribe), [userInfo.subscribe]);
  const progressBarStyle = useMemo(() => getAssetStorageProgressBarStyle(info), [info]);
  const loggedIn = isUserLoggedIn();
  const actionText = info.isHighestLevel
    ? languageStore.smartcatTranslationText((v) => v.contact_us)
    : languageStore.smartcatTranslationText((v) => v.upgrade_capacity_1);

  useEffect(() => {
    requestedSubscribeRef.current = false;
  }, [userInfo.uid]);

  useEffect(() => {
    if (!loggedIn || info.isReady || requestedSubscribeRef.current) return;
    requestedSubscribeRef.current = true;
    loadSubscribeInfo().catch(() => {
      message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    });
  }, [info.isReady, languageStore, loadSubscribeInfo, loggedIn, userInfo.uid]);

  /** 首页展开态容量卡每次从隐藏切到可见时上报一次曝光。 */
  useEffect(() => {
    const shouldExpose = variant === 'expanded' && visible && loggedIn && info.isReady;
    if (shouldExpose && !exposedRef.current) {
      trackRoboneoVipShow('asset_left_upgrade');
    }
    exposedRef.current = shouldExpose;
  }, [info.isReady, loggedIn, variant, visible]);

  /** 点击容量 CTA：最高等级会员打开反馈，其他会员打开首页来源的会员订阅弹窗。 */
  const handleAction = useCallback(() => {
    const latestInfo = getAssetStorageInfo(useOfficialStore.getState().userInfo.subscribe);
    if (latestInfo.isHighestLevel) {
      setFeedbackOpen(true);
      return;
    }
    trackRoboneoVipClick('asset_left_upgrade');
    openVipModal('home_page', undefined, { from_source: 'asset_left_upgrade' });
  }, []);

  if (!loggedIn || !info.isReady) return null;

  if (variant === 'compact') {
    return (
      <>
        <div className={classNames(styles.navbarAssetStorageCompact, className, {
          [styles.navbarAssetStorageCompactExceeded]: info.isExceeded,
        })}
        >
          <div className={styles.navbarAssetStorageCompactText}>
            <span>{languageStore.smartcatTranslationText((v) => v.capacity_1)}</span>
            <span>{`${Math.round(info.progressPercent)}%`}</span>
          </div>
          <div className={styles.navbarAssetStorageCompactUsage}>
            <div className={styles.navbarAssetStorageCompactTrack}>
              <span className={styles.navbarAssetStorageCompactBar} style={progressBarStyle} />
            </div>
          </div>
          <div className={styles.navbarAssetStorageCompactPopover}>
            <div className={styles.navbarAssetStorageCompactPopoverContent}>
              <div className={styles.navbarAssetStorageCompactPopoverRow}>
                <span>{languageStore.smartcatTranslationText((v) => v.capacity_1)}</span>
                <span>{info.usageLabel}</span>
              </div>
              <div className={styles.navbarAssetStorageCompactPopoverTrack}>
                <span className={styles.navbarAssetStorageCompactPopoverBar} style={progressBarStyle} />
              </div>
              <button
                type="button"
                className={styles.navbarAssetStorageCompactPopoverButton}
                onClick={handleAction}
              >
                {actionText}
              </button>
            </div>
          </div>
        </div>
        <FeedbackModal
          userInfo={userInfo}
          env={getNavbarAssetStorageFeedbackEnv()}
          client_id={getEnv().CLIENT_ID}
          gid={getGid()}
          channel=""
          language={languageStore.languageCode}
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          getAccessToken={getAccessToken}
        />
      </>
    );
  }

  return (
    <>
      <div className={classNames(styles.navbarAssetStorageCard, className, {
        [styles.navbarAssetStorageCardExceeded]: info.isExceeded,
      })}
      >
        <div className={styles.navbarAssetStorageContent}>
          <div className={styles.navbarAssetStorageRow}>
            <span>{languageStore.smartcatTranslationText((v) => v.capacity_1)}</span>
            <span>{info.usageLabel}</span>
          </div>
          <div className={styles.navbarAssetStorageTrack}>
            <span className={styles.navbarAssetStorageBar} style={progressBarStyle} />
          </div>
          <button
            type="button"
            className={classNames(styles.navbarAssetStorageButton, {
              [styles.navbarAssetStorageButtonPrimary]: info.isHighestLevel,
            })}
            onClick={handleAction}
          >
            {actionText}
          </button>
        </div>
      </div>
      <FeedbackModal
        userInfo={userInfo}
        env={getNavbarAssetStorageFeedbackEnv()}
        client_id={getEnv().CLIENT_ID}
        gid={getGid()}
        channel=""
        language={languageStore.languageCode}
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        getAccessToken={getAccessToken}
      />
    </>
  );
}

export default NavbarAssetStorageCard;
